import React from "react";
import { observer } from "mobx-react-lite";
import { CPLN_HELM_RELEASE_PREFIX, homeLink, linksOf, request } from "../../../services/cpln";
import { ConsoleContext } from "../../../mobxStores/consoleContext/consoleContext";
import { Route, useParams } from "react-router-dom";
import { decodeRevisionFrom, fetchReleaseItems } from "../utils";
import { ngParseLink } from "../../../utils/linkParser/linkParser";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { MarketplaceReleaseInstance, ReleaseRevision } from "../types/release";
import { DetailLayout } from "../../../components/detail/layout";
import { DetailContext } from "../../../components/detail/detailContext";
import { ReleaseInfo } from "./info";
import { ReleaseUpgrade } from "./upgrade";
import { ReleaseRevisions } from "./revisions";
import { Loader } from "../../../components/layout/loader";
import NGAlert from "../../../newcomponents/alert";
import { Secret } from "../../../schema/types/secret";
import { IS_DEPLOYMENT_ENV_PROD } from "../../../envVariables";
import { Template } from "../types/template";

type RouteParams = "release";
const MarketplaceReleaseDetailRaw: React.FC = () => {
  const { release: releaseName } = useParams<RouteParams>();

  const basePathUpdated = useBasePath(`/marketplace/release/${releaseName}/*`);

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [template, setTemplate] = React.useState<Template>(null as any);
  const [release, setRelease] = React.useState<MarketplaceReleaseInstance>(null as any);

  const [items, setItems] = React.useState<any[]>([]);
  const [itemsNotFound, setItemsNotFound] = React.useState<any[]>([]);
  const [revisions, setRevisions] = React.useState<ReleaseRevision[]>([]);
  const [revisionStateSecretName, setRevisionStateSecretName] = React.useState("");

  React.useEffect(() => {
    fetchRelease();
  }, []);

  React.useEffect(() => {
    if (!release) {
      return;
    }

    fetchTemplate();
  }, [release]);

  React.useEffect(() => {
    if (!release) {
      return;
    }

    if (revisions.length < 1) {
      return;
    }

    fetchItems();
  }, [revisions]);

  React.useEffect(() => {
    if (!release) {
      return;
    }

    // TODO Get agent health
    // TODO Get volumeset info
  }, [items]);

  async function fetchRelease() {
    try {
      setIsLoading(true);

      let _items: Secret[] = [];
      let nextLink: string | undefined = undefined;

      const url = homeLink("secret") + "/-query";
      const body = {
        kind: "secret",
        spec: {
          match: "all",
          terms: [
            { op: "~", property: "name", value: `${CPLN_HELM_RELEASE_PREFIX}` },
            { op: "exists", tag: "cpln/marketplace" },
            { op: "=", tag: "name", value: `${releaseName}` },
          ],
        },
      };
      const { data } = await request({ method: "post", url: url, body: body });
      _items = data.items;
      nextLink = linksOf(data).next;

      while (nextLink) {
        const { data } = await request({ url: nextLink, body: body });
        nextLink = linksOf(data).next;
        _items = _items.concat(data.items);
      }
      const secrets = _items
        .filter((i) => i.name.startsWith(`${CPLN_HELM_RELEASE_PREFIX}`))
        .filter((i) => (i.tags || {}).name === releaseName);

      const templateName = (secrets[0].tags || {})["cpln/marketplace-template"];

      const promises: Promise<any>[] = secrets.map((s) => {
        const selfLink = linksOf(s).self!;
        return request({ url: selfLink + "/-reveal" });
      });

      const revealedSecrets: Secret[] = (await Promise.allSettled(promises))
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value.data);

      const revisions = revealedSecrets.map(decodeRevisionFrom).filter(Boolean) as ReleaseRevision[];
      setRevisions(revisions);

      const sortedRevisions = revisions.toSorted((a, b) => {
        if (a.version > b.version) return -1;
        if (b.version > a.version) return 1;
        return 0;
      });
      const firstRevision = sortedRevisions[sortedRevisions.length - 1];
      const lastRevision = sortedRevisions[0];

      const lastRevisionIndex = Math.max(
        revisions.findIndex((r) => r.version === lastRevision.version),
        0,
      );
      const lastRevisionSecret = secrets[lastRevisionIndex];
      const templateVersion = (lastRevisionSecret?.tags || {})["cpln/marketplace-template-version"];

      setRevisionStateSecretName(revealedSecrets[lastRevisionIndex]?.name || "");

      setRelease({
        kind: "marketplace-release",
        lastModified: lastRevision.info.lastDeployed,
        created: firstRevision.info.firstDeployed,
        // where do I use this?
        tags: {},
        gvc: lastRevision.gvc || "",
        org: ConsoleContext.org!,
        name: releaseName!,
        id: releaseName!,
        template: templateName,
        version: templateVersion,
        revision: lastRevision.version,
      });
      setIsLoading(false);
    } catch (e) {
      let errorMessage = e.response?.data?.error;
      if (!errorMessage) errorMessage = e.message;
      setError(errorMessage);
      setIsLoading(false);
    }
  }

  async function fetchTemplate() {
    try {
      const { data } = await request<Template>({ service: "marketplace", url: `/template/${release.template}` });
      setTemplate(data);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) {
        errorMessage = e.message;
      }
      setError(errorMessage);
      console.error(`Marketplace App Detail Info Fetch Template`, errorMessage);
    }
  }

  async function fetchItems() {
    try {
      const revision = revisions.toSorted((a, b) => {
        if (a.version > b.version) return -1;
        if (b.version > a.version) return 1;
        return 0;
      })[0];
      const _itemResults = await fetchReleaseItems(revision.info.resources);
      const _itemsNotFound = _itemResults.filter((_i) => _i.notFound === true);
      const _items = _itemResults.filter((_i) => !_i.notFound);

      const kindOrder = [
        "gvc",
        "workload",
        "volumeset",
        "identity",
        "domain",
        "agent",
        "secret",
        "cloudaccount",
        "group",
        "serviceaccount",
        "policy",
        "auditctx",
      ];

      let _sortedItems: any[] = [];
      for (const kind of kindOrder) {
        _sortedItems = _sortedItems.concat(..._items.filter((i) => i.kind === kind));
      }
      setItems(_sortedItems);
      let _sortedItemsNotFound: any[] = [];
      for (const kind of kindOrder) {
        _sortedItemsNotFound = _sortedItemsNotFound.concat(..._itemsNotFound.filter((i) => i.kind === kind));
      }
      setItemsNotFound(_sortedItemsNotFound);
      setIsLoading(false);
    } catch (e) {
      let errorMessage = e.response?.data?.error;
      if (!errorMessage) errorMessage = e.message;
      setError(errorMessage);
      setIsLoading(false);
    }
  }

  const usedGvcs: string[] = [];
  for (const item of items) {
    const { isValid, name: gvcName } = ngParseLink(linksOf(item).gvc || "", { useInputCtx: true });
    if (isValid && !usedGvcs.includes(gvcName)) {
      usedGvcs.push(gvcName);
    }
  }

  if (error) {
    return (
      <>
        <NGAlert type={"error"} title="Error" message={`${error}`} />
        {IS_DEPLOYMENT_ENV_PROD ? null : (
          <div className="flex flex-col gap-4 text-sm">
            <div>Release</div>
            <pre>{JSON.stringify(release, null, 2)}</pre>
            <div>Revisions</div>
            <pre>{JSON.stringify(revisions, null, 2)}</pre>
          </div>
        )}
      </>
    );
  }

  if (!release || !template || isLoading) {
    return <Loader reason={"Fetching data"} />;
  }

  return (
    <>
      <BasePathContext.Provider value={basePathUpdated}>
        <DetailContext.Provider value={{ item: release, fetchItem: fetchRelease }}>
          <DetailLayout
            links={[
              { label: "Info", url: "-info" },
              { label: "Upgrade", url: "-upgrade" },
              { label: "Revisions", url: "-revisions" },
            ]}
          >
            <Route
              path={`-info`}
              element={
                <ReleaseInfo
                  template={template}
                  release={release}
                  usedGvcs={usedGvcs}
                  items={items}
                  itemsNotFound={itemsNotFound}
                />
              }
            />
            <Route
              path={`-upgrade`}
              element={
                <ReleaseUpgrade
                  template={template}
                  release={release}
                  currentRevisionSecretName={revisionStateSecretName}
                />
              }
            />
            <Route path={`-revisions`} element={<ReleaseRevisions release={release} revisions={revisions} />} />
          </DetailLayout>
        </DetailContext.Provider>
      </BasePathContext.Provider>
    </>
  );
};

export const MarketplaceReleaseDetail = observer(MarketplaceReleaseDetailRaw);
